import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  TextField,
  Collapse,
  Alert,
  Button,
  Popover,
  Tooltip,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LabelIcon from '@mui/icons-material/Label';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import { create } from '@bufbuild/protobuf';
import {
  ReceiptItemSchema,
  SaleItemSchema,
  type UploadReceiptResponse,
  type GetReceiptResponse,
} from '../../generated/receipt/v1/receipt_pb';
import { useReceiptStore } from '../store/receiptStore';
import { PaymentIcon } from './PaymentIcon';
import { normalizeStoreName } from '@/utils/textUtils';
import { setItemDescriptionMapping, deleteItemDescriptionMapping } from '@/api/receipt';
import { QuantityInput } from './QuantityInput';
import { PriceInput } from './PriceInput';

// Default item categories
const DEFAULT_CATEGORIES = [
  'Produce',
  'Dairy',
  'Meat & Seafood',
  'Bakery',
  'Frozen',
  'Pantry',
  'Beverages',
  'Snacks',
  'Household',
  'Other',
] as const;

// Local interfaces for edit state (plain objects, not protobuf Message instances)
interface EditableReceiptItem {
  description: string;
  price: number;
  quantity?: number;
  unitPrice?: number;
  category?: string;
}

interface EditableSaleItem {
  item: string;
  discount: number;
}

interface Props {
  data: UploadReceiptResponse | GetReceiptResponse;
  onItemChange?: (updatedData: UploadReceiptResponse | GetReceiptResponse) => void;
}

export const ReceiptDataDisplay: React.FC<Props> = ({ data, onItemChange }) => {
  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const MAX_INPUT_CHARS = 500;
  const getCharLimitHelper = (value: string) =>
    value.length >= MAX_INPUT_CHARS ? `${MAX_INPUT_CHARS}/${MAX_INPUT_CHARS} characters` : undefined;
  const handleEditKeyDown = (event: React.KeyboardEvent, onSave: () => void, onCancel: () => void) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSave();
    }
  };
  const setReceiptData = useReceiptStore((state) => state.setReceiptData);

  const [isEditingStore, setIsEditingStore] = useState(false);
  const [editedStore, setEditedStore] = useState('');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editedDate, setEditedDate] = useState<dayjs.Dayjs | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedItem, setEditedItem] = useState<EditableReceiptItem | null>(null);
  const [itemsExpanded, setItemsExpanded] = useState(true);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [draggingItemIndex, setDraggingItemIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<EditableReceiptItem>({
    description: '',
    price: 0,
    quantity: 1,
    unitPrice: 0,
    category: '',
  });
  const [editingSaleIndex, setEditingSaleIndex] = useState<number | null>(null);
  const [editedSaleItem, setEditedSaleItem] = useState<EditableSaleItem | null>(null);
  const [isAddingSaleItem, setIsAddingSaleItem] = useState(false);
  const [draggingSaleIndex, setDraggingSaleIndex] = useState<number | null>(null);
  const [discountsExpanded, setDiscountsExpanded] = useState(data.saleItems.length > 0);
  const [editedPriceInput, setEditedPriceInput] = useState('');
  const [newItemPriceInput, setNewItemPriceInput] = useState('0');
  const [editedSaleDiscountInput, setEditedSaleDiscountInput] = useState('');
  const [newSaleDiscountInput, setNewSaleDiscountInput] = useState('0');
  const [newSaleItem, setNewSaleItem] = useState<EditableSaleItem>({
    item: '',
    discount: 0,
  });

  // Item tagging state
  const [taggingAnchorEl, setTaggingAnchorEl] = useState<HTMLElement | null>(null);
  const [taggingItemIndex, setTaggingItemIndex] = useState<number | null>(null);
  const [customDescription, setCustomDescription] = useState('');
  const [isSavingTag, setIsSavingTag] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  const reconciliation = useMemo(() => {
    const itemsTotal = data.items.reduce((sum, item) => sum + item.price, 0);
    const discountsTotal = data.saleItems.reduce((sum, sale) => sum + Math.abs(sale.discount), 0);
    const calculatedTotal = itemsTotal - discountsTotal + data.tax;
    const difference = Math.abs(calculatedTotal - data.total);
    const isReconciled = difference < 0.01;

    return {
      itemsTotal,
      discountsTotal,
      calculatedTotal,
      difference,
      isReconciled,
    };
  }, [data.items, data.saleItems, data.tax, data.total]);

  const discountsHeaderTotal = useMemo(
    () => data.saleItems.reduce((sum, sale) => sum + sale.discount, 0),
    [data.saleItems]
  );

  const handleEditStoreClick = () => {
    setIsEditingStore(true);
    setEditedStore(data.store);
  };

  const handleCancelStoreEdit = () => {
    setIsEditingStore(false);
    setEditedStore('');
  };

  const handleSaveStoreEdit = () => {
    if (!editedStore.trim()) return;

    const updatedData = {
      ...data,
      store: editedStore.trim(),
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    setIsEditingStore(false);
    setEditedStore('');
  };

  const handleEditDateClick = () => {
    setIsEditingDate(true);
    // Parse the existing date string, or use today if no date
    const parsed = data.date ? dayjs(data.date) : dayjs();
    setEditedDate(parsed.isValid() ? parsed : dayjs());
  };

  const handleCancelDateEdit = () => {
    setIsEditingDate(false);
    setEditedDate(null);
  };

  const handleSaveDateEdit = () => {
    if (!editedDate) return;

    const updatedData = {
      ...data,
      date: editedDate.format('YYYY-MM-DD'),
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    setIsEditingDate(false);
    setEditedDate(null);
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    const item = data.items[index];
    const quantity = item.quantity ?? 1;
    const unitPrice = item.unitPrice ?? item.price / quantity;
    setEditedItem({
      ...item,
      unitPrice,
      category: item.category || '',
    });
    setEditedPriceInput(item.price.toString());
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedItem(null);
    setEditedPriceInput('');
  };

  const handleSaveEdit = (index: number) => {
    if (!editedItem) return;

    const parsedPrice = parseFloat(editedPriceInput);
    const price = Number.isFinite(parsedPrice) ? parsedPrice : 0;
    const quantity = editedItem.quantity ?? 1;
    const unitPrice = price / quantity;

    const originalItem = data.items[index];
    const updatedItems = [...data.items];
    updatedItems[index] = create(ReceiptItemSchema, {
      description: originalItem.description,
      customDescription: originalItem.customDescription,
      price,
      quantity,
      unitPrice,
      category: editedItem.category || undefined,
    });

    const updatedData = {
      ...data,
      items: updatedItems,
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    onItemChange?.(updatedData);
    setEditingIndex(null);
    setEditedItem(null);
    setEditedPriceInput('');
  };

  const handleFieldChange = (field: keyof EditableReceiptItem, value: string) => {
    if (!editedItem) return;

    if (field === 'quantity') {
      const newQuantity = parseFloat(value) || 1;
      // Unit price was set when editing started (original price / original quantity)
      // Multiply unit price by new quantity to get new total price
      const unitPrice = editedItem.unitPrice ?? (parseFloat(editedPriceInput) || 0);
      const newPrice = unitPrice * newQuantity;
      setEditedItem({
        ...editedItem,
        quantity: newQuantity,
        unitPrice,
        price: newPrice,
      });
      setEditedPriceInput(newPrice.toFixed(2));
    } else if (field === 'price') {
      // Store as string to preserve decimal input
      setEditedPriceInput(value);
      // Update unit price when price is manually changed
      const newPrice = parseFloat(value) || 0;
      const quantity = editedItem.quantity ?? 1;
      setEditedItem({
        ...editedItem,
        price: newPrice,
        unitPrice: newPrice / quantity,
      });
    } else {
      setEditedItem({
        ...editedItem,
        [field]: parseFloat(value) || 0,
      });
    }
  };

  const handleDeleteItem = (index: number) => {
    const updatedItems = data.items.filter((_, i) => i !== index);
    const updatedData = {
      ...data,
      items: updatedItems,
    } as UploadReceiptResponse;
    setReceiptData(updatedData);
    onItemChange?.(updatedData);
    setEditingIndex(null);
    setEditedItem(null);
  };

  const handleAddItemClick = () => {
    setIsAddingItem(true);
    setNewItem({
      description: '',
      price: 0,
      quantity: 1,
      unitPrice: 0,
      category: '',
    });
    setNewItemPriceInput('0');
  };

  const handleCancelAddItem = () => {
    setIsAddingItem(false);
    setNewItem({
      description: '',
      price: 0,
      quantity: 1,
      unitPrice: 0,
      category: '',
    });
    setNewItemPriceInput('0');
  };

  const handleSaveNewItem = () => {
    if (!newItem.description.trim()) return;

    const parsedPrice = parseFloat(newItemPriceInput);
    const price = Number.isFinite(parsedPrice) ? parsedPrice : 0;
    const quantity = newItem.quantity || 1;
    const unitPrice = price / quantity;

    const itemToAdd = create(ReceiptItemSchema, {
      description: newItem.description,
      price,
      quantity,
      unitPrice,
      category: newItem.category || undefined,
    });

    const updatedItems = [...data.items, itemToAdd];
    const updatedData = {
      ...data,
      items: updatedItems,
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    onItemChange?.(updatedData);
    setIsAddingItem(false);
    setNewItem({
      description: '',
      price: 0,
      quantity: 1,
      unitPrice: 0,
      category: '',
    });
    setNewItemPriceInput('0');
  };

  const handleNewItemFieldChange = (field: keyof EditableReceiptItem, value: string) => {
    if (field === 'description') {
      setNewItem({
        ...newItem,
        description: value,
      });
    } else if (field === 'quantity') {
      const newQuantity = parseFloat(value) || 1;
      // Use existing unit price if set, otherwise calculate from current price / current quantity
      const currentPrice = parseFloat(newItemPriceInput) || 0;
      const currentQty = newItem.quantity ?? 1;
      const unitPrice = newItem.unitPrice && newItem.unitPrice > 0
        ? newItem.unitPrice
        : currentPrice / currentQty;
      const newPrice = unitPrice * newQuantity;
      setNewItem({
        ...newItem,
        quantity: newQuantity,
        unitPrice,
        price: newPrice,
      });
      setNewItemPriceInput(newPrice.toFixed(2));
    } else if (field === 'price') {
      // Store as string to preserve decimal input
      setNewItemPriceInput(value);
      // Update unit price when price is manually changed
      const newPrice = parseFloat(value) || 0;
      const quantity = newItem.quantity ?? 1;
      setNewItem({
        ...newItem,
        price: newPrice,
        unitPrice: newPrice / quantity,
      });
    }
  };

  const reorderList = <T,>(list: T[], fromIndex: number, toIndex: number) => {
    const updated = [...list];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    return updated;
  };

  const handleItemDragStart = (index: number) => {
    setDraggingItemIndex(index);
  };

  const handleItemDrop = (index: number) => {
    if (draggingItemIndex === null || draggingItemIndex === index) {
      setDraggingItemIndex(null);
      return;
    }

    const updatedItems = reorderList(data.items, draggingItemIndex, index);
    const updatedData = {
      ...data,
      items: updatedItems,
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    setDraggingItemIndex(null);
  };

  const handleEditSaleClick = (index: number) => {
    setEditingSaleIndex(index);
    setEditedSaleItem({
      ...data.saleItems[index],
    });
    setEditedSaleDiscountInput(`${data.saleItems[index].discount ?? 0}`);
  };

  useEffect(() => {
    if (data.saleItems.length === 0 && !isAddingSaleItem) {
      setDiscountsExpanded(false);
    }
  }, [data.saleItems.length, isAddingSaleItem]);

  const handleCancelSaleEdit = () => {
    setEditingSaleIndex(null);
    setEditedSaleItem(null);
    setEditedSaleDiscountInput('');
  };

  const handleSaveSaleEdit = (index: number) => {
    if (!editedSaleItem) return;

    const parsedDiscount = parseFloat(editedSaleDiscountInput);
    const discount = Number.isFinite(parsedDiscount) ? parsedDiscount : 0;
    const updatedSaleItems = [...data.saleItems];
    updatedSaleItems[index] = create(SaleItemSchema, {
      item: editedSaleItem.item,
      discount,
    });

    const updatedData = {
      ...data,
      saleItems: updatedSaleItems,
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    onItemChange?.(updatedData);
    setEditingSaleIndex(null);
    setEditedSaleItem(null);
  };

  const handleSaleFieldChange = (field: keyof EditableSaleItem, value: string) => {
    if (!editedSaleItem) return;

    if (field === 'item') {
      setEditedSaleItem({
        ...editedSaleItem,
        item: value,
      });
    } else {
      setEditedSaleDiscountInput(value);
    }
  };

  const handleDeleteSaleItem = (index: number) => {
    const updatedSaleItems = data.saleItems.filter((_, i) => i !== index);
    const updatedData = {
      ...data,
      saleItems: updatedSaleItems,
    } as UploadReceiptResponse;
    setReceiptData(updatedData);
    onItemChange?.(updatedData);
    setEditingSaleIndex(null);
    setEditedSaleItem(null);
  };

  const handleAddSaleItemClick = () => {
    setIsAddingSaleItem(true);
    setDiscountsExpanded(true);
    setNewSaleDiscountInput('0');
    setNewSaleItem({
      item: '',
      discount: 0,
    });
  };

  const handleCancelAddSaleItem = () => {
    setIsAddingSaleItem(false);
    setNewSaleDiscountInput('0');
    setNewSaleItem({
      item: '',
      discount: 0,
    });
  };

  const handleSaveNewSaleItem = () => {
    if (!newSaleItem.item.trim()) return;

    const parsedDiscount = parseFloat(newSaleDiscountInput);
    const discount = Number.isFinite(parsedDiscount) ? parsedDiscount : 0;
    const saleToAdd = create(SaleItemSchema, {
      item: newSaleItem.item,
      discount,
    });

    const updatedSaleItems = [...data.saleItems, saleToAdd];
    const updatedData = {
      ...data,
      saleItems: updatedSaleItems,
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    onItemChange?.(updatedData);
    setIsAddingSaleItem(false);
    setNewSaleDiscountInput('0');
    setNewSaleItem({
      item: '',
      discount: 0,
    });
  };

  const handleNewSaleFieldChange = (field: keyof EditableSaleItem, value: string) => {
    if (field === 'item') {
      setNewSaleItem({
        ...newSaleItem,
        item: value,
      });
    } else {
      setNewSaleDiscountInput(value);
    }
  };

  const handleSaleDragStart = (index: number) => {
    setDraggingSaleIndex(index);
  };

  const handleSaleDrop = (index: number) => {
    if (draggingSaleIndex === null || draggingSaleIndex === index) {
      setDraggingSaleIndex(null);
      return;
    }

    const updatedSaleItems = reorderList(data.saleItems, draggingSaleIndex, index);
    const updatedData = {
      ...data,
      saleItems: updatedSaleItems,
    } as UploadReceiptResponse;

    setReceiptData(updatedData);
    setDraggingSaleIndex(null);
  };

  // Item tagging handlers
  const handleOpenTagging = (event: React.MouseEvent<HTMLElement>, index: number) => {
    const item = data.items[index];
    setTaggingAnchorEl(event.currentTarget);
    setTaggingItemIndex(index);
    // Use existing custom description if available, otherwise empty for new tag
    setCustomDescription(item.customDescription || '');
    setTagError(null);
  };

  const handleCloseTagging = () => {
    setTaggingAnchorEl(null);
    setTaggingItemIndex(null);
    setCustomDescription('');
    setTagError(null);
  };

  const handleSaveTag = async () => {
    if (taggingItemIndex === null) return;

    const item = data.items[taggingItemIndex];
    const originalDescription = item.description;

    if (!customDescription.trim()) {
      setTagError('Custom description is required');
      return;
    }

    try {
      setIsSavingTag(true);
      setTagError(null);

      await setItemDescriptionMapping({
        originalDescription,
        customDescription: customDescription.trim(),
      });

      // Update local state with the custom description
      const updatedItems = [...data.items];
      updatedItems[taggingItemIndex] = create(ReceiptItemSchema, {
        ...item,
        customDescription: customDescription.trim(),
      });

      const updatedData = {
        ...data,
        items: updatedItems,
      } as UploadReceiptResponse;

      setReceiptData(updatedData);
      handleCloseTagging();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Failed to save tag');
    } finally {
      setIsSavingTag(false);
    }
  };

  const handleRemoveTag = async () => {
    if (taggingItemIndex === null) return;

    const item = data.items[taggingItemIndex];
    const originalDescription = item.description;

    try {
      setIsSavingTag(true);
      setTagError(null);

      await deleteItemDescriptionMapping({
        originalDescription,
      });

      // Update local state to remove the custom description
      const updatedItems = [...data.items];
      updatedItems[taggingItemIndex] = create(ReceiptItemSchema, {
        description: item.description,
        price: item.price,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        category: item.category,
        // customDescription is not set, removing it
      });

      const updatedData = {
        ...data,
        items: updatedItems,
      } as UploadReceiptResponse;

      setReceiptData(updatedData);
      handleCloseTagging();
    } catch (err) {
      setTagError(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setIsSavingTag(false);
    }
  };

  const taggingOpen = Boolean(taggingAnchorEl);
  const taggingItem = taggingItemIndex !== null ? data.items[taggingItemIndex] : null;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', width: '100%', maxWidth: '100%' }}>
      <Stack spacing={2} sx={{ maxWidth: '100%' }}>
        {/* Store Information */}
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
            >
              Store Information
            </Typography>
            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap">
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, minWidth: 50, mr: 1 }}>
                  Store:
                </Typography>
                <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                  {isEditingStore ? (
                    <>
                      <TextField
                        size="small"
                        value={editedStore}
                        onChange={(e) => setEditedStore(e.target.value)}
                        helperText={getCharLimitHelper(editedStore)}
                        onKeyDown={(event) => handleEditKeyDown(event, handleSaveStoreEdit, handleCancelStoreEdit)}
                        sx={{ flex: 1, minWidth: 0 }}
                        autoFocus
                      />
                      <IconButton size="small" color="primary" onClick={handleSaveStoreEdit} aria-label="save">
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelStoreEdit} aria-label="cancel">
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }} noWrap>
                        {normalizeStoreName(data.store)}
                      </Typography>
                      <IconButton size="small" onClick={handleEditStoreClick} aria-label="edit store">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap">
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, minWidth: 50, mr: 1 }}>
                  Date:
                </Typography>
                <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                  {isEditingDate ? (
                    <>
                      <DatePicker
                        value={editedDate}
                        onChange={(newValue) => setEditedDate(newValue)}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { flex: 1, minWidth: 0 },
                            onKeyDown: (event: React.KeyboardEvent) =>
                              handleEditKeyDown(event, handleSaveDateEdit, handleCancelDateEdit),
                          },
                        }}
                      />
                      <IconButton size="small" color="primary" onClick={handleSaveDateEdit} aria-label="save">
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleCancelDateEdit} aria-label="cancel">
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }} noWrap>
                        {data.date || 'N/A'}
                      </Typography>
                      <IconButton size="small" onClick={handleEditDateClick} aria-label="edit date">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="nowrap">
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, minWidth: 50, mr: 1 }}>
                  Payment:
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                  {data.payment ? (
                    <PaymentIcon paymentMethod={data.payment} />
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }} noWrap>
                      N/A
                    </Typography>
                  )}
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Items Table */}
        <Card sx={{ overflow: 'hidden', maxWidth: '100%' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 }, overflow: 'hidden', maxWidth: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
              >
                Items
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                  {formatCurrency(reconciliation.itemsTotal)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setItemsExpanded(!itemsExpanded)}
                  aria-label={itemsExpanded ? 'collapse items' : 'expand items'}
                >
                  {itemsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={itemsExpanded}>
              {/* Mobile view - Card-based layout */}
              <Stack spacing={1} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                {data.items.map((item, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                    {editingIndex === index ? (
                      // Mobile edit mode
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {item.customDescription ? (
                              <Tooltip title={`Original: ${item.description}`} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'primary.main', fontWeight: 500 }}
                                >
                                  {item.customDescription}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2">{item.description}</Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Qty:</Typography>
                            <QuantityInput
                              value={editedItem?.quantity ?? 1}
                              onChange={(value) => handleFieldChange('quantity', String(value))}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Price:</Typography>
                            <PriceInput
                              value={editedPriceInput}
                              onChange={(value) => handleFieldChange('price', value)}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Category:</Typography>
                          <Autocomplete
                            size="small"
                            freeSolo
                            options={DEFAULT_CATEGORIES}
                            value={editedItem?.category || ''}
                            onChange={(_, newValue) => {
                              if (editedItem) {
                                setEditedItem({ ...editedItem, category: newValue || '' });
                              }
                            }}
                            onInputChange={(_, newValue) => {
                              if (editedItem) {
                                setEditedItem({ ...editedItem, category: newValue || '' });
                              }
                            }}
                            slotProps={{ paper: { sx: { minWidth: 150 } } }}
                            sx={{ flex: 1, minWidth: 0 }}
                            renderInput={(params) => (
                              <TextField {...params} placeholder="Category" size="small" />
                            )}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveEdit(index)}
                          >
                            Save
                          </Button>
                        </Box>
                      </Stack>
                    ) : (
                      // Mobile view mode
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            {item.customDescription ? (
                              <Tooltip title={`Original: ${item.description}`} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'primary.main', fontWeight: 500, wordBreak: 'break-word' }}
                                >
                                  {item.customDescription}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {item.description}
                              </Typography>
                            )}
                            <Tooltip title={item.customDescription ? 'Edit tag' : 'Tag this item'}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenTagging(e, index)}
                                sx={{ p: 0.25, color: item.customDescription ? 'primary.main' : 'action.active' }}
                              >
                                <LabelIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="body2" fontWeight={600} sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                            {formatCurrency(item.price)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                              Qty: {item.quantity ?? 1}
                            </Typography>
                            {item.category && (
                              <Typography variant="caption" color="text.secondary">
                                {item.category}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handleEditClick(index)} aria-label="edit" sx={{ p: 0.5 }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteItem(index)} aria-label="delete" sx={{ p: 0.5 }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ))}
                {isAddingItem && (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        placeholder="Item description"
                        value={newItem.description}
                        onChange={(e) => handleNewItemFieldChange('description', e.target.value)}
                        helperText={getCharLimitHelper(newItem.description)}
                        onKeyDown={(event) => handleEditKeyDown(event, handleSaveNewItem, handleCancelAddItem)}
                        autoFocus
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Qty:</Typography>
                          <QuantityInput
                            value={newItem.quantity ?? 1}
                            onChange={(value) => handleNewItemFieldChange('quantity', String(value))}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Price:</Typography>
                          <PriceInput
                            value={newItemPriceInput}
                            onChange={(value) => handleNewItemFieldChange('price', value)}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Category:</Typography>
                        <Autocomplete
                          size="small"
                          freeSolo
                          options={DEFAULT_CATEGORIES}
                          value={newItem.category || ''}
                          onChange={(_, newValue) => setNewItem({ ...newItem, category: newValue || '' })}
                          onInputChange={(_, newValue) => setNewItem({ ...newItem, category: newValue || '' })}
                          slotProps={{ paper: { sx: { minWidth: 150 } } }}
                          sx={{ flex: 1, minWidth: 0 }}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="Category" size="small" />
                          )}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
                        <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelAddItem}>
                          Cancel
                        </Button>
                        <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveNewItem}>
                          Save
                        </Button>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Stack>

              {/* Desktop view - Table layout */}
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', display: { xs: 'none', sm: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 40 }} />
                      <TableCell sx={{ minWidth: 120, px: 2 }}>Description</TableCell>
                      <TableCell align="right" sx={{ width: 50, px: 2 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ width: 80, px: 2 }}>Price</TableCell>
                      <TableCell sx={{ width: 100, px: 2 }}>Category</TableCell>
                      <TableCell align="right" sx={{ width: 80, px: 2 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.items.map((item, index) => (
                      <TableRow
                        key={index}
                        draggable={!isAddingItem}
                        onDragStart={() => handleItemDragStart(index)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleItemDrop(index)}
                        onDragEnd={() => setDraggingItemIndex(null)}
                        sx={{ cursor: isAddingItem ? 'default' : 'grab' }}
                      >
                        <TableCell padding="checkbox">
                          <DragIndicatorIcon
                            fontSize="small"
                            color={isAddingItem ? 'disabled' : 'action'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150, wordBreak: 'break-word', px: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {item.customDescription ? (
                                <Tooltip title={`Original: ${item.description}`} arrow>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: 'inherit',
                                      color: 'primary.main',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {item.customDescription}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                item.description
                              )}
                            </Box>
                            {editingIndex !== index && (
                              <Tooltip title={item.customDescription ? 'Edit tag' : 'Tag this item'}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenTagging(e, index)}
                                  sx={{
                                    p: 0.25,
                                    color: item.customDescription ? 'primary.main' : 'action.active',
                                  }}
                                >
                                  <LabelIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ px: 2 }}>
                          {editingIndex === index ? (
                            <QuantityInput
                              value={editedItem?.quantity ?? 1}
                              onChange={(value) => handleFieldChange('quantity', String(value))}
                            />
                          ) : (
                            item.quantity ?? 1
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ px: 2 }}>
                          {editingIndex === index ? (
                            <PriceInput
                              value={editedPriceInput}
                              onChange={(value) => handleFieldChange('price', value)}
                            />
                          ) : (
                            formatCurrency(item.price)
                          )}
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          {editingIndex === index ? (
                            <Autocomplete
                              size="small"
                              freeSolo
                              options={DEFAULT_CATEGORIES}
                              value={editedItem?.category || ''}
                              onChange={(_, newValue) => {
                                if (editedItem) {
                                  setEditedItem({ ...editedItem, category: newValue || '' });
                                }
                              }}
                              onInputChange={(_, newValue) => {
                                if (editedItem) {
                                  setEditedItem({ ...editedItem, category: newValue || '' });
                                }
                              }}
                              slotProps={{
                                paper: {
                                  sx: { minWidth: 150 },
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Category"
                                  sx={{ minWidth: 120 }}
                                />
                              )}
                            />
                          ) : (
                            item.category || '—'
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ px: 2 }}>
                          {editingIndex === index ? (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSaveEdit(index)}
                                aria-label="save"
                                sx={{ p: 0.5 }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                aria-label="cancel"
                                sx={{ p: 0.5 }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(index)}
                                aria-label="edit"
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteItem(index)}
                                aria-label="delete"
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {isAddingItem && (
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 40 }} />
                        <TableCell sx={{ maxWidth: 150, px: 2 }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            placeholder="Item description"
                            value={newItem.description}
                            onChange={(e) => handleNewItemFieldChange('description', e.target.value)}
                            helperText={getCharLimitHelper(newItem.description)}
                            onKeyDown={(event) =>
                              handleEditKeyDown(event, handleSaveNewItem, handleCancelAddItem)
                            }
                            autoFocus
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ px: 2 }}>
                          <QuantityInput
                            value={newItem.quantity ?? 1}
                            onChange={(value) => handleNewItemFieldChange('quantity', String(value))}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ px: 2 }}>
                          <PriceInput
                            value={newItemPriceInput}
                            onChange={(value) => handleNewItemFieldChange('price', value)}
                          />
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Autocomplete
                            size="small"
                            freeSolo
                            options={DEFAULT_CATEGORIES}
                            value={newItem.category || ''}
                            onChange={(_, newValue) => {
                              setNewItem({ ...newItem, category: newValue || '' });
                            }}
                            onInputChange={(_, newValue) => {
                              setNewItem({ ...newItem, category: newValue || '' });
                            }}
                            slotProps={{
                              paper: {
                                sx: { minWidth: 150 },
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Category"
                                sx={{ minWidth: 120 }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ px: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleSaveNewItem}
                              aria-label="save"
                              sx={{ p: 0.5 }}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelAddItem}
                              aria-label="cancel"
                              sx={{ p: 0.5 }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />}
                  variant="outlined"
                  size="small"
                  onClick={handleAddItemClick}
                  disabled={isAddingItem}
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Add Item
                </Button>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Sale Items */}
        <Card sx={{ overflow: 'hidden', maxWidth: '100%' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 }, overflow: 'hidden', maxWidth: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
              >
                Discounts
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                  {formatCurrency(discountsHeaderTotal)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setDiscountsExpanded(!discountsExpanded)}
                  aria-label={discountsExpanded ? 'collapse discounts' : 'expand discounts'}
                  disabled={data.saleItems.length === 0 && !isAddingSaleItem}
                >
                  {discountsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
            </Box>
            <Collapse in={discountsExpanded}>
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: { xs: 0, sm: 40 }, display: { xs: 'none', sm: 'table-cell' } }} />
                      <TableCell sx={{ minWidth: { xs: 100, sm: 120 }, px: { xs: 1, sm: 2 } }}>Item</TableCell>
                      <TableCell align="right" sx={{ width: { xs: 80, sm: 120 }, px: { xs: 1, sm: 2 } }}>Discount</TableCell>
                      <TableCell align="right" sx={{ width: { xs: 70, sm: 80 }, px: { xs: 1, sm: 2 } }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.saleItems.map((sale, index) => (
                      <TableRow
                        key={index}
                        draggable={!isAddingSaleItem}
                        onDragStart={() => handleSaleDragStart(index)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleSaleDrop(index)}
                        onDragEnd={() => setDraggingSaleIndex(null)}
                        sx={{ cursor: isAddingSaleItem ? 'default' : 'grab' }}
                      >
                        <TableCell padding="checkbox" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <DragIndicatorIcon
                            fontSize="small"
                            color={isAddingSaleItem ? 'disabled' : 'action'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: { xs: 120, sm: 150 }, wordBreak: 'break-word', px: { xs: 1, sm: 2 } }}>
                          {editingSaleIndex === index ? (
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              value={editedSaleItem?.item || ''}
                              onChange={(e) => handleSaleFieldChange('item', e.target.value)}
                              helperText={getCharLimitHelper(editedSaleItem?.item ?? '')}
                              onKeyDown={(event) =>
                                handleEditKeyDown(event, () => handleSaveSaleEdit(index), handleCancelSaleEdit)
                              }
                            />
                          ) : (
                            sale.item
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>
                          {editingSaleIndex === index ? (
                            <PriceInput
                              value={editedSaleDiscountInput}
                              onChange={(value) => handleSaleFieldChange('discount', value)}
                              allowNegative
                            />
                          ) : (
                            formatCurrency(sale.discount)
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>
                          {editingSaleIndex === index ? (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSaveSaleEdit(index)}
                                aria-label="save"
                                sx={{ p: 0.5 }}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelSaleEdit}
                                aria-label="cancel"
                                sx={{ p: 0.5 }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditSaleClick(index)}
                                aria-label="edit"
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteSaleItem(index)}
                                aria-label="delete"
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {isAddingSaleItem && (
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 40, display: { xs: 'none', sm: 'table-cell' } }} />
                        <TableCell sx={{ maxWidth: { xs: 120, sm: 150 }, px: { xs: 1, sm: 2 } }}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            placeholder="Discount item"
                            value={newSaleItem.item}
                            onChange={(e) => handleNewSaleFieldChange('item', e.target.value)}
                            helperText={getCharLimitHelper(newSaleItem.item)}
                            onKeyDown={(event) =>
                              handleEditKeyDown(event, handleSaveNewSaleItem, handleCancelAddSaleItem)
                            }
                            autoFocus
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>
                          <PriceInput
                            value={newSaleDiscountInput}
                            onChange={(value) => handleNewSaleFieldChange('discount', value)}
                            allowNegative
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleSaveNewSaleItem}
                              aria-label="save"
                              sx={{ p: 0.5 }}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelAddSaleItem}
                              aria-label="cancel"
                              sx={{ p: 0.5 }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }} />}
                  variant="outlined"
                  size="small"
                  onClick={handleAddSaleItemClick}
                  disabled={isAddingSaleItem}
                  sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Add Discount
                </Button>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
            >
              Summary
            </Typography>
            <Stack spacing={1}>
              {!reconciliation.isReconciled && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}>
                    Total Mismatch
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', md: '0.75rem' } }}>
                    Items total: {formatCurrency(reconciliation.itemsTotal)}
                    {reconciliation.discountsTotal > 0 && ` - Discounts: ${formatCurrency(reconciliation.discountsTotal)}`}
                    {` + Tax: ${formatCurrency(data.tax)} = ${formatCurrency(reconciliation.calculatedTotal)}`}
                  </Typography>
                  <br />
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.75rem', md: '0.75rem' } }}>
                    Difference: {formatCurrency(reconciliation.difference)}
                  </Typography>
                </Alert>
              )}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  Tax:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  {formatCurrency(data.tax)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                  Total:
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
                >
                  {formatCurrency(data.total)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Item Tagging Popover */}
      <Popover
        open={taggingOpen}
        anchorEl={taggingAnchorEl}
        onClose={handleCloseTagging}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 320 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Tag Item
          </Typography>
          {taggingItem && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Original: {taggingItem.description}
            </Typography>
          )}
          {tagError && (
            <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
              <Typography variant="caption">{tagError}</Typography>
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            label="Custom description"
            placeholder="e.g., Milk, Bread, Eggs..."
            value={customDescription}
            onChange={(e) => setCustomDescription(e.target.value)}
            helperText={getCharLimitHelper(customDescription)}
            disabled={isSavingTag}
            onKeyDown={(event) => handleEditKeyDown(event, handleSaveTag, handleCloseTagging)}
            sx={{ mb: 1.5 }}
            autoFocus
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            This tag will be applied to all receipts with this item.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {taggingItem?.customDescription && (
              <Button
                size="small"
                color="error"
                startIcon={isSavingTag ? <CircularProgress size={14} /> : <LabelOffIcon />}
                onClick={handleRemoveTag}
                disabled={isSavingTag}
              >
                Remove
              </Button>
            )}
            <Button
              size="small"
              onClick={handleCloseTagging}
              disabled={isSavingTag}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={isSavingTag ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveTag}
              disabled={isSavingTag || !customDescription.trim()}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};
